"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ReactPaginate from "react-paginate";
import { formatDate } from "@/utils/helper";
import { ImageWithLoader } from "../ui";
import { useTransition } from "react";
import dynamic from "next/dynamic";
import { getBlogPageLimit } from "./blogPagination";

interface BlogListingProps {
  blog: any;
  categories: any;
}

const BlogListing = ({ blog, categories }: BlogListingProps) => {
  const [isPending, startTransition] = useTransition();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { data, ...rest } = blog;
  const pagination = rest;
  const pageLimit = getBlogPageLimit(pagination?.limit);
  const [isNavigating, setIsNavigating] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Initialize selected categories from URL
  useEffect(() => {
    const filter = searchParams?.get("filter");
    if (filter) {
      setSelectedCategories(filter.split(","));
    } else {
      setSelectedCategories([]);
    }
  }, [searchParams]);

  // Memoize the navigation handler
  const handleNavigation = useCallback(
    (selected: string[]) => {
      setIsNavigating(true);
      startTransition(() => {
        const filterParam = selected.length > 0 ? selected.join(",") : "";
        router.push(`/blog?page=1&limit=${pageLimit}&filter=${filterParam}`);
        setIsNavigating(false);
      });
    },
    [router, pageLimit]
  );

  // Handle category toggle
  const handleCategoryToggle = (categoryId: string) => {
    let updatedCategories: string[];
    if (categoryId === "") {
      // "All" clears all selections
      updatedCategories = [];
    } else if (selectedCategories.includes(categoryId)) {
      // Deselect category
      updatedCategories = selectedCategories.filter((id) => id !== categoryId);
    } else {
      // Select category
      updatedCategories = [...selectedCategories, categoryId];
    }
    setSelectedCategories(updatedCategories);
    handleNavigation(updatedCategories);
  };

  // Handle page change
  const handlePageChange = useCallback(
    (page: any) => {
      setIsNavigating(true);
      startTransition(() => {
        const filterParam =
          selectedCategories.length > 0 ? selectedCategories.join(",") : "";
        router.push(
          `/blog?page=${page?.selected + 1}&limit=${pageLimit}&filter=${filterParam}`
        );
        setIsNavigating(false);
      });
    },
    [router, pageLimit, selectedCategories]
  );

  return (
    <div className="tabs -pills-3 pt-30 js-tabs">
      <div className="tabs__controls row x-gap-10 y-gap-10 justify-center js-tabs-controls">
        <div className="col-auto" key={1}>
          <button
            className={`tabs__button text-14 fw-500 px-20 py-10 rounded-4 bg-light-2 js-tabs-button ${
              selectedCategories.length === 0 ? "is-tab-el-active" : ""
            }`}
            onClick={() => handleCategoryToggle("")}
            disabled={isNavigating}
          >
            {"All"}
          </button>
        </div>
        {categories?.map((option: any) => (
          <div className="col-auto" key={option?.id}>
            <button
              className={`tabs__button text-14 fw-500 px-20 py-10 rounded-4 bg-light-2 js-tabs-button ${
                selectedCategories.includes(option?.id) ? "is-tab-el-active" : ""
              }`}
              onClick={() => handleCategoryToggle(option?.id)}
              disabled={isNavigating}
            >
              {option?.name}
            </button>
          </div>
        ))}
      </div>

      {/* Show skeleton loader when loading */}
      {isNavigating || isPending ? (
        <div className="row y-gap-30 pt-30">
          {Array(9)
            .fill(0)
            .map((_, index) => (
              <div className="col-lg-4 col-sm-6" key={index}>
                <div className="blogCard -type-1 d-block">
                  <div className="blogCard__image">
                    <Skeleton height={300} />
                  </div>
                  <div className="pt-20">
                    <Skeleton height={24} width={200} />
                    <Skeleton height={20} width={100} />
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <>
          {data?.length > 0 ? (
            <div className="row y-gap-30 pt-30">
              {data?.map((item: any) => (
                <div className="col-lg-4 col-sm-6" key={item?.id}>
                  <Link
                    href={`/blog/${item?.slug}`}
                    className="blogCard -type-1 d-block"
                  >
                    <div className="blogCard__image">
                      <div>
                        <ImageWithLoader
                          width={400}
                          height={300}
                          className="cover w-100 img-fluid"
                          src={
                            item?.post_image_url ||
                            "https://placehold.co/300x300/png"
                          }
                          alt="image"
                          style={{ minHeight: "277px" }}
                          showLoader={isNavigating || isPending}
                        />
                      </div>
                    </div>
                    <div className="pt-20">
                      <h4 className="text-dark-1 text-18 fw-500">
                        {item?.title}
                      </h4>
                      <div className="text-light-1 text-15 lh-14 mt-5">
                        {formatDate(item?.created_at, "dd MMM yyyy")}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-50">
              <h4 className="text-18 fw-500">No blogs found</h4>
            </div>
          )}
        </>
      )}

      {pagination?.total_pages > 1 && (
        <div className={isNavigating ? "opacity-50 pointer-events-none" : ""}>
          <ReactPaginate
            containerClassName={"pagination"}
            onPageChange={handlePageChange}
            pageCount={pagination?.total_pages}
            nextLabel=">"
            previousLabel="<"
            breakLabel="..."
            pageRangeDisplayed={4}
            marginPagesDisplayed={1}
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
          />
        </div>
      )}
    </div>
  );
};

export default dynamic(() => Promise.resolve(BlogListing), { ssr: true });
